/**
 * UI/UX's screens and components on the board, pure: a screen is a top-level frame that is not a
 * component; a component is a frame marked `style.component`, made once and copied by instances.
 */
import { contains, type BoardContent, type BoardFrame, type Point } from "../../state/board";

/** Whether a frame is a component (`style.component`): a part made once and copied by instances. */
export function isComponent(frame: BoardFrame): boolean {
  return frame.group.style?.component === "yes";
}

/** The screens of a board: its top-level frames that are not components, left to right, then top to bottom. */
export function screensOf(content: BoardContent): BoardFrame[] {
  return content.frames.filter((f) => f.depth === 0 && !isComponent(f)).sort((a, b) => a.place.x - b.place.x || a.place.y - b.place.y);
}

/** The screen under `p` (a top-level frame, not a component), if any. */
export function screenAt(content: BoardContent, p: Point): string | null {
  return screensOf(content).find((f) => contains(f.place, p))?.id ?? null;
}

/** The screen the prototype starts on among `screens`: the one marked `style.start`, else the first. */
export function startOf(screens: readonly BoardFrame[]): BoardFrame | undefined {
  return screens.find((s) => s.group.style?.start === "yes") ?? screens[0];
}
